import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  buildContext,
  isEvaluatorAtAll,
  canManageTemplates,
  canManageEvaluators,
  adminDistrictIds,
  scopeDistrictIds,
  grantDistrictIds,
  IMPLICIT_ALL_ROLES,
  IMPLICIT_DISTRICT_ROLES,
} from "../model/permissions";
import { SCOPE_TYPES, EVAL_VIEWS } from "../model/constants";
import { list } from "../api/scaffold";
import {
  listAdditionalDistricts,
  listPortfolioDistricts,
  listGrantsForUser,
  listSitesForAreas,
  listDirectReports,
  listDistricts,
  areaSitesMap,
} from "../api/evaluatorsApi";

// Who may do what in /evaluations (spec §8, pattern usePortfolioAccess).
//
//   { loading, ctx, canEvaluate, canBuild, canManage, hasOwn, districtOptions, error, reload }
//
// Roles 1/2/8 qualify by role. Role 2/8 still read their AdditionalSchoolDistrict rows so
// `ctx` knows their whole district set. Everyone else qualifies only through
// EvalV2_EvaluatorGrant rows (plus the area sites / direct reports those grants point at).
// `hasOwn` (My Evaluations) is one `limit=1` read of their own completed evaluations.
//
// The sidebar, the top nav and the module shell all call this hook, so the resolved access
// is cached per user for a few minutes and in-flight loads are shared: three callers cost
// one set of reads. Every read is wrapped; a failed read degrades to "no access from that
// source", never a thrown render. Requests per load: role 1 -> 2, role 2/8 -> <=3,
// granted users -> <=5.

const TTL_MS = 5 * 60 * 1000;
let cache = null; // { key, at, promise, value }

export function __resetEvaluationAccessCache() {
  cache = null;
}

const EMPTY = Object.freeze({
  ctx: null,
  canEvaluate: false,
  canBuild: false,
  canManage: false,
  hasOwn: false,
  districtOptions: [],
  error: null,
});

const dataOr = (res, fallback) => (res && res.ok ? res.data : fallback);

async function resolveAccess(login) {
  const roleId = Number(login?.roleId ?? login?.RoleId);
  const userId = login?.userId ?? login?.UserId;
  const errors = [];
  const note = (res) => {
    if (res && !res.ok && !res.notFound && res.error) errors.push(res.error);
    return res;
  };

  const ownP = list(EVAL_VIEWS.evaluations, { EvaluateeUserId: userId, Status: "Completed", limit: 1 }).then(note);

  let additionalDistrictIds = [];
  let portfolioDistrictIds = [];
  let grants = [];
  let areaSites = {};
  let directReportIds = [];

  if (IMPLICIT_ALL_ROLES.includes(roleId)) {
    // Super Admin: nothing to load for scope.
  } else if (IMPLICIT_DISTRICT_ROLES.includes(roleId)) {
    const [addRes, portRes] = await Promise.all([listAdditionalDistricts(userId), listPortfolioDistricts(userId)]);
    additionalDistrictIds = dataOr(note(addRes), []);
    portfolioDistrictIds = dataOr(note(portRes), []);
  } else {
    grants = dataOr(note(await listGrantsForUser(userId)), []);
    const areaIds = grants.filter((g) => g.ScopeType === SCOPE_TYPES.AREA && g.AreaId).map((g) => g.AreaId);
    const wantsReports = grants.some((g) => g.ScopeType === SCOPE_TYPES.REPORTS);
    const [sitesRes, reportsRes] = await Promise.all([
      areaIds.length ? listSitesForAreas(areaIds).then(note) : Promise.resolve(null),
      wantsReports ? listDirectReports(userId).then(note) : Promise.resolve(null),
    ]);
    areaSites = areaSitesMap(dataOr(sitesRes, []));
    directReportIds = dataOr(reportsRes, []);
  }

  const ctx = buildContext({ login, additionalDistrictIds, portfolioDistrictIds, grants, areaSites, directReportIds });

  // District picker options: everything for Super Admin; otherwise only when more than
  // one district is in play (a single-district user never sees the picker). Site / Area /
  // Reports grants contribute their grant's district too: it is not in their evaluable
  // scope as a whole district, but it is the district their scoped rows are read from —
  // without it a grant-only evaluator has no district at all and every page reads nothing.
  const adminIds = adminDistrictIds(ctx);
  const scopeIds = scopeDistrictIds(ctx);
  const pickerIds =
    adminIds === null ? null : [...new Set([...(adminIds || []), ...(scopeIds || []), ...grantDistrictIds(ctx)])];
  let districtOptions = [];
  if (pickerIds === null || pickerIds.length > 1) {
    const all = dataOr(note(await listDistricts()), []);
    districtOptions =
      pickerIds === null ? all : all.filter((d) => pickerIds.includes(String(d.id).trim().toUpperCase()));
  } else if (pickerIds.length === 1) {
    districtOptions = [{ id: pickerIds[0], name: null }];
  }

  const ownRes = await ownP;
  return {
    ctx,
    canEvaluate: isEvaluatorAtAll(ctx),
    canBuild: canManageTemplates(ctx, null),
    canManage: canManageEvaluators(ctx),
    hasOwn: dataOr(ownRes, []).length > 0,
    districtOptions,
    error: errors.length ? errors[0] : null,
  };
}

function loadAccess(login, force) {
  const key = `${login?.userId ?? login?.UserId}|${login?.roleId ?? login?.RoleId}|${login?.schoolDistrictId ?? login?.SchoolDistrictId}`;
  const fresh = cache && cache.key === key && Date.now() - cache.at < TTL_MS;
  if (!force && fresh) return cache.promise;
  const entry = { key, at: Date.now(), promise: null };
  entry.promise = resolveAccess(login).catch((err) => ({ ...EMPTY, ctx: buildContext({ login }), error: err?.message || "Could not load evaluation access." }));
  cache = entry;
  return entry.promise;
}

export default function useEvaluationAccess() {
  const login = useSelector((s) => s?.login?.LoginDetails);
  const userId = login?.userId ?? login?.UserId;
  const [state, setState] = useState(() => ({ loading: Boolean(userId), ...EMPTY }));
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!userId) {
      setState({ loading: false, ...EMPTY });
      return undefined;
    }
    let alive = true;
    setState((s) => (s.loading ? s : { ...s, loading: true }));
    loadAccess(login, tick > 0).then((value) => {
      if (alive) setState({ loading: false, ...value });
    });
    return () => {
      alive = false;
    };
    // login fields that change access are in the key; the object identity is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, login?.roleId, login?.RoleId, login?.schoolDistrictId, login?.SchoolDistrictId, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, reload };
}
